'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Conditional, Modal, Portal } from 'components/pcc-components';
import { useAssociatedDealer } from 'components/associated-dealers/associated-dealer-context';
import { LocationAddress } from 'services/location/types';
import { DealerResponse } from 'services/dealer';
import {
  DealerAssociationResponse,
  DealerErrorResponse
} from 'services/dealer-association';
import { useAssociatedDealerForms } from 'components/associated-dealers/hooks/use-associated-dealer-forms';
import { SelectCustomerForm } from '../select-customer-form/select-customer-form';
import LoaderWithOverlay from 'app/[lang]/[esite]/(core)/account/(with-nav)/control-center/common/LoaderWithOverlay';
import { ROOT_PORTALS } from 'constants/targets';

type AssociatedDealerModalContentProps = {
  isLoggedIn: boolean;
  isDealerUser: boolean;
  isInstantAccessUser: boolean;
  isCspUser: boolean;
  dealerUserResponse: DealerResponse;
  registeredUserResponse: DealerAssociationResponse | DealerErrorResponse;
  storeAddress: LocationAddress;
  openAdpModal: boolean;
};

export const AssociatedDealerModalContent = ({
  isLoggedIn,
  isDealerUser,
  isCspUser,
  isInstantAccessUser,
  dealerUserResponse,
  registeredUserResponse,
  storeAddress,
  openAdpModal
}: AssociatedDealerModalContentProps) => {
  const t = useTranslations();
  const { isLoading, associatedDealerModal } = useAssociatedDealer();
  const { dealerSearchForm, selectCustomerForm } = useAssociatedDealerForms();
  const isAdpModalActive =
    !isLoading && !associatedDealerModal.modalControl.isActive && openAdpModal;

  /**
   * * if user changes esite by modifying the URL, they will be in the wrong site,
   * * this prompts the user to select a location so they get redirected to the correct esite
   */
  useEffect(() => {
    if (isAdpModalActive) {
      associatedDealerModal.modalControl.open();
    }
  }, [isAdpModalActive, associatedDealerModal]);

  return (
    <Portal id={ROOT_PORTALS}>
      <Modal {...associatedDealerModal.modalProps}>
        <Conditional test={associatedDealerModal.modalProps.isActive}>
          <SelectCustomerForm
            isModal={true}
            dealerSearchForm={dealerSearchForm}
            selectCustomerForm={selectCustomerForm}
            isDealerUser={isDealerUser}
            isInstantAccessUser={isInstantAccessUser}
            isCspUser={isCspUser}
            isLoggedIn={isLoggedIn}
            dealerUserResponse={dealerUserResponse}
            registeredUserResponse={registeredUserResponse}
            storeAddress={storeAddress}
            wrongSite={openAdpModal}
          />
        </Conditional>
      </Modal>
      <Conditional test={isLoading}>
        <LoaderWithOverlay
          message={t('LOADING')}
          cardClassName="px-2 py-2 align-items-center cat-u-theme-typography-label"
          messageClassName="ps-2"
        />
      </Conditional>
    </Portal>
  );
};
